import BreadCrumb from "Common/BreadCrumb";
import Loader from "Common/Components/Loader";
import TableContainer from "Common/TableContainer";
import { APIClient } from "helpers/api_helper";
import { Search } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "Common/SafeLink";
import Select from "react-select";
import { toast } from "react-toastify";
import { CreateWinners } from "services/apiGameSlots";
import { useGameSlots } from "../gameSlot/useGameSlot";

const api = new APIClient();

const AutoDraw = () => {
  const { gameSlots } = useGameSlots();
  const [selectedWinners, setSelectedWinners] = useState([]);
  const [numWinners, setNumWinners] = useState(0);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ticketList, setTicketList] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [drawType, setDrawType] = useState("Ticket ID");

  useEffect(() => {
    if (gameSlots) {
      const formattedOptions = gameSlots
        .filter((item) => item.type === "RADIO" && item.state !== "CLOSED") // Filter by type and state
        .map((item) => ({
          label: item.slotTitle, // Use slotTitle as the label
          value: item.id, // Use id as the value
          numberOfWinners: item.numberOfWinners, // Include additional data if needed
        }));
      setOptions(formattedOptions); // Set the filtered and formatted options
    }
  }, [gameSlots]);

  const columns = useMemo(
    () => [
      {
        header: drawType === "Ticket ID" ? "Ticket ID" : "Phone ID",
        accessorKey: drawType === "Ticket ID" ? "ticketNumber" : "phoneNumber",
        enableColumnFilter: false,
      },
    ],
    [drawType]
  );

  const fetchSlotTickets = async (selectedOption) => {
    setLoading(true);
    try {
      const response = await api.get(
        `merchant/tickets/${selectedOption.value}`
      );
      setTicketList(response.data);
      setNumWinners(selectedOption.numberOfWinners);
    } catch (err) {
      toast.error("Failed to load ticket details");
    } finally {
      setLoading(false);
    }
  };

  const selectRandomWinner = (ref) => {
    const randIndex = Math.floor(Math.random() * ticketList.length);
    const selected = ticketList[randIndex];
    if (ref.some((item) => item.id === selected.id)) {
      return selectRandomWinner(ref);
    }
    return selected;
  };

  const handleWinnerSelection = (count) => {
    if (count > ticketList.length) {
      toast.error("Number of winners cannot be greater than total tickets");
      return;
    }
    const selectedWins = [];
    for (let i = 1; i <= count; i++) {
      const selected = selectRandomWinner(selectedWins);
      selectedWins.push(selected);
    }
    setSelectedWinners(selectedWins);
  };

  const handleApproveAll = async () => {
    if (selectedWinners.length === 0) {
      toast.error("No winners selected to approve.");
      return;
    }

    if (!selectedOption) {
      toast.error("No game slot selected.");
      return;
    }

    // Prepare data for the API call
    const winnersData = selectedWinners.map((winner) => ({
      ticketNumber: winner.ticketNumber,
    }));

    try {
      await CreateWinners(winnersData, selectedOption.value);
      toast.success("All selected winners approved successfully!");
      setSelectedWinners([]); // Clear winners after approval
    } catch (err) {
      toast.error(`An error occurred: ${err.message}`);
    }
  };

  return (
    <React.Fragment>
      <BreadCrumb title="Draws" pageTitle="draws" />
      <div className="grid grid-cols-1 lg:grid-cols-12 xl:grid-cols-12 gap-x-5">
        <div className="lg:col-span-12 xl:col-span-3 xl:row-span-2">
          <div className="mb-5">
            <label
              htmlFor="deliveryStatusSelect"
              className="inline-block mb-2 text-base font-medium"
            >
              Select Game Slot
            </label>
            <Select
              className="border-slate-200 focus:outline-none focus:border-custom-500"
              options={options}
              value={selectedOption}
              isSearchable={true}
              name="deliveryStatusSelect"
              id="deliveryStatusSelect"
              onChange={(option) => {
                setSelectedOption(option);
                fetchSlotTickets(option);
              }}
            />
          </div>
          <div className="card">
            {loading ? (
              <Loader />
            ) : (
              <div className="card-body">
                {ticketList ? (
                  <div className="mt-5 overflow-x-auto">
                    <table className="w-full mb-0">
                      <tbody>
                        {ticketList.map((item, i) => (
                          <tr key={i}>
                            <td className="px-3.5 py-2.5 border-y font-semibold">
                              {drawType === "Ticket ID"
                                ? item.ticketNumber
                                : item.phoneNumber}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p>Loading...</p>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="xl:col-span-9 lg:col-span-12">
          <div className="card">
            <div className="card-body flex space-x-4">
              <input
                type="number"
                value={numWinners}
                readOnly
                placeholder="Number of Winners"
                className="px-5 py-2 text-15 form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100"
              />
              <Link
                to="#"
                type="button"
                className="text-white btn bg-custom-500 border-custom-500"
                onClick={() => handleWinnerSelection(numWinners)}
              >
                <span className="align-middle">Generate</span>
              </Link>
            </div>
          </div>
          <div>
            {selectedWinners && selectedWinners.length > 0 ? (
              <TableContainer
                isPagination={false}
                columns={columns}
                data={selectedWinners}
                customPageSize={10}
                divclassName="overflow-x-auto"
                tableclassName="w-full border-separate table-custom border-spacing-y-2 whitespace-nowrap"
              />
            ) : (
              <div className="noresult">
                <div className="py-6 text-center">
                  <Search className="size-6 mx-auto text-sky-500 fill-sky-100" />
                  <h5 className="mt-2 mb-1">Sorry! No Winners here yet</h5>
                  <p className="mb-0 text-slate-500">
                    We did not find any attendance for your search.
                  </p>
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <Link
                to="#"
                type="button"
                className="text-white btn bg-custom-500 border-custom-500"
                onClick={handleApproveAll}
              >
                Approve All
              </Link>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default AutoDraw;
